<template>
  <div class="chat-messages-list" ref="messagesList">
    <div v-if="messages.length === 0" class="empty-state">
      <feather-icon icon="MessageCircleIcon" size="64" class="text-muted mb-1" />
      <h4 class="text-muted">{{ emptyStateMessage }}</h4>
      <p class="text-muted">{{ emptyStateDescription }}</p>
    </div>

    <div v-else class="messages-container">
      <transition-group name="message-fade">
        <div
          v-for="message in messages"
          :key="message.uid"
          class="message-wrapper"
          :class="message.sender"
        >
          <!-- Message Header (outside bubble) -->
          <div class="message-header">
            <b-avatar
              :variant="message.sender === 'user' ? 'light-primary' : 'light-success'"
              size="32"
            >
              <feather-icon
                :icon="message.sender === 'user' ? 'UserIcon' : 'CpuIcon'"
                size="18"
              />
            </b-avatar>
            <div class="message-meta">
              <span class="sender-name">
                {{ message.sender === 'user' ? 'Vous' : 'Assistant IA' }}
              </span>
              <span class="message-time">{{ formatTime(message.created_at) }}</span>
            </div>
          </div>

          <!-- Message Bubble (colored) -->
          <div class="message-bubble" :class="{ 'streaming': message.isStreaming }">
            <div v-if="message.isStreaming" class="streaming-content">
              <span v-html="formatMessage(message.content)"></span>
              <span class="cursor-blink">|</span>
            </div>
            <div v-else v-html="formatMessage(message.content)"></div>
          </div>

          <!-- Message Actions (outside bubble, for assistant only) -->
          <div v-if="!message.isStreaming && message.sender === 'assistant'" class="message-actions">
            <b-button
              variant="flat-secondary"
              size="sm"
              @click="copyMessage(message.content)"
              v-b-tooltip.hover
              title="Copier"
            >
              <feather-icon icon="CopyIcon" size="14" />
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              @click="$emit('regenerate', message.id)"
              v-b-tooltip.hover
              title="Régénérer"
            >
              <feather-icon icon="RefreshCwIcon" size="14" />
            </b-button>
          </div>
        </div>
      </transition-group>

      <!-- Typing Indicator -->
      <div v-if="isTyping && !isStreaming" class="message-wrapper assistant">
        <div class="message typing-indicator">
          <b-avatar variant="light-success" size="32" class="mr-75">
            <feather-icon icon="CpuIcon" size="18" />
          </b-avatar>
          <div class="typing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll to Bottom Button -->
    <transition name="fade">
      <b-button
        v-if="showScrollButton"
        variant="primary"
        class="scroll-to-bottom"
        size="sm"
        pill
        @click="scrollToBottom"
      >
        <feather-icon icon="ArrowDownIcon" size="18" />
      </b-button>
    </transition>
  </div>
</template>

<script>
import { BAvatar, BButton, VBTooltip } from 'bootstrap-vue'

import moment from "moment";
moment.locale('fr-FR')

import { marked } from 'marked'

export default {
  name: 'ChatMessagesList',
  components: {
    BAvatar,
    BButton
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  props: {
    messages: {
      type: Array,
      required: true,
      default: () => []
    },
    isStreaming: {
      type: Boolean,
      default: false
    },
    isTyping: {
      type: Boolean,
      default: false
    },
    chatType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showScrollButton: false,
      scrollObserver: null
    }
  },
  computed: {
    emptyStateMessage() {
      return this.chatType === 'rapport'
        ? 'Commencez votre rapport'
        : 'Créez votre CV'
    },
    emptyStateDescription() {
      return this.chatType === 'rapport'
        ? 'Chargez vos fichiers et posez vos questions pour générer un rapport détaillé'
        : 'Chargez votre modèle et répondez aux questions pour créer votre CV'
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    }
  },
  mounted() {
    this.setupScrollObserver()
    this.scrollToBottom()
  },
  beforeDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
  },
  methods: {
    setupScrollObserver() {
      const container = this.$refs.messagesList
      if (!container) return

      // Show/hide scroll button based on scroll position
      container.addEventListener('scroll', () => {
        const { scrollTop, scrollHeight, clientHeight } = container
        this.showScrollButton = scrollHeight - scrollTop - clientHeight > 200
      })
    },
    scrollToBottom(smooth = true) {
      this.$nextTick(() => {
        const container = this.$refs.messagesList
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: smooth ? 'smooth' : 'auto'
          })
        }
      })
    },
    formatMessage(content) {
      try {
        // Configure marked options
        marked.setOptions({
          breaks: true,
          gfm: true
        })
        return marked(content || '')
      } catch (error) {
        console.error('Error formatting message:', error)
        return content
      }
    },
    formatTime(date) {
      return moment(date, 'YYYY-MM-DD H:m:s').fromNow();
    },
    async copyMessage(content) {
      try {
        await navigator.clipboard.writeText(content)
        this.$toast({
          component: this.$createElement('div', {}, [
            this.$createElement('feather-icon', {
              props: { icon: 'CheckIcon' },
              class: 'mr-50'
            }),
            'Message copié'
          ]),
          props: {
            variant: 'success'
          }
        })
      } catch (error) {
        console.error('Error copying message:', error)
        this.$toast({
          component: this.$createElement('div', {}, [
            this.$createElement('feather-icon', {
              props: { icon: 'XIcon' },
              class: 'mr-50'
            }),
            'Erreur lors de la copie'
          ]),
          props: {
            variant: 'danger'
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-messages-list {
  height: 100%;
  overflow-y: auto;
  padding: 1.5rem;
  position: relative;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;

    &:hover {
      background: #555;
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
}

.messages-container {
  display: flex;
  flex-direction: column;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 3rem;
  animation: messageSlideIn 0.3s ease;
  max-width: 95%;

  &.user {
    align-self: flex-end;
    align-items: flex-end;

    .message-header {
      flex-direction: row-reverse;
      margin-bottom: 0.5rem;

      .message-meta {
        text-align: right;
        margin-right: 0.5rem;
      }
    }

    .message-bubble {
      background: linear-gradient(135deg, var(--primary) 0%, #7367f0 100%);
      color: white;
      border-radius: 18px 18px 4px 18px;
    }
  }

  &.assistant {
    align-self: flex-start;
    align-items: flex-start; // Aligne tout le contenu à gauche

    .message-header {
      margin-bottom: 0.5rem;

      .message-meta {
        margin-left: 0.5rem;
      }
    }

    .message-bubble {
      background: #f8f8f8;
      color: #333;
      border-radius: 18px 18px 18px 4px;
    }

    .message-actions {
      margin-top: 0.5rem;
      margin-left: 42px; // Align with message bubble
    }
  }
}

.message-header {
  display: flex;
  align-items: center;
}

.message-meta {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  .sender-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: #5e5873;
  }

  .message-time {
    font-size: 0.75rem;
    color: #b9b9c3;
  }
}

.message-bubble {
  padding: 0.875rem 1.125rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  line-height: 1.6;
  word-wrap: break-word;
  max-width: 100%;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  }

  &.streaming {
    animation: pulse 1.5s ease-in-out infinite;
  }

  ::v-deep {
    p {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    code {
      background: rgba(0, 0, 0, 0.1);
      padding: 0.2rem 0.4rem;
      border-radius: 4px;
      font-size: 0.9em;
    }

    pre {
      background: rgba(0, 0, 0, 0.05);
      padding: 1rem;
      border-radius: 8px;
      overflow-x: auto;
      margin: 0.5rem 0;

      code {
        background: none;
        padding: 0;
      }
    }

    ul, ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    blockquote {
      border-left: 4px solid var(--primary);
      padding-left: 1rem;
      margin: 0.5rem 0;
      font-style: italic;
    }
  }

  // Style spécifique pour les bulles utilisateur
  .message-wrapper.user & {
    ::v-deep {
      code {
        background: rgba(255, 255, 255, 0.2);
      }

      pre {
        background: rgba(255, 255, 255, 0.15);
      }

      blockquote {
        border-left-color: rgba(255, 255, 255, 0.5);
      }
    }
  }
}

.streaming-content {
  display: inline;
}

.cursor-blink {
  display: inline-block;
  animation: blink 1s step-end infinite;
  color: var(--primary);
  font-weight: bold;
}

.message-actions {
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  .message-wrapper:hover & {
    opacity: 1;
  }
}

.typing-dots {
  display: flex;
  gap: 0.25rem;
  padding: 0.5rem;

  span {
    width: 8px;
    height: 8px;
    background: var(--primary);
    border-radius: 50%;
    animation: typingDot 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

.scroll-to-bottom {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

// Animations
@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  50% {
    box-shadow: 0 4px 16px rgba(115, 103, 240, 0.3);
  }
}

@keyframes blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}

@keyframes typingDot {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.message-fade-enter-active,
.message-fade-leave-active {
  transition: all 0.3s ease;
}

.message-fade-enter,
.message-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
